import { FC } from 'react';

import { PunchupStatTileGrid } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { SendPool } from 'features/PunchupPages/SendTickets/poolBudget';

import { SendTicketsPoolTile } from './SendTicketsPoolTile';

export interface SendTicketsPoolTilesProps {
	/** Pools (with sendable budget) from `buildSendPoolBudget` — render nothing when empty. */
	pools: SendPool[];
	/**
	 * Pool ids whose requested quantity across their member tiers exceeds the pool's remaining capacity.
	 * Renders a red ring on the affected tile so the manager sees the conflict live — the pool-level analogue
	 * of `TicketAvailabilityTiles`' per-type ring. Meaningful in bulk mode (the single-recipient steppers are
	 * pool-clamped, so over-reach can't be entered there). Omit/empty when nothing is over budget.
	 */
	exceededPoolIds?: Set<number>;
}

/**
 * Read-only pool row for the Send Tickets left column, rendered with the SAME stat-tile layout as
 * `TicketAvailabilityTiles` (pool name as the title, the available count as the big value, member tiers as a
 * chip row below) so pools and ticket types read consistently on this availability-focused page. This is a
 * deliberate divergence from the Show-Dashboard pool tile (`PunchupPoolTile`, an editable `{used}/{total}`
 * entity card): the dashboard tile is an editable entity, the Send tile is a read-only availability stat.
 *
 * The headline number is `sellableRemaining` (the shared remaining the on-sale tiers can actually draw),
 * not raw `remaining`, so an oversized pool never overstates what can be sent. A legacy over-subscribed pool
 * (issued > total) shows a warning icon + the shared over-subscribed tooltip; a live over-reach (bulk mode)
 * shows the red ring via `exceededPoolIds`.
 *
 * Each pool renders through `SendTicketsPoolTile` so the tile's `tagSlot`/`topRight`/`helpButton` nodes are
 * memoised per pool (the memo-d `PunchupTile` would otherwise re-render on every parent render). The colour
 * resolver is read once here and passed down so the hook runs a single time.
 */
export const SendTicketsPoolTiles: FC<SendTicketsPoolTilesProps> = ({ pools, exceededPoolIds }) => {
	// Tint each member chip with its ticket type's colour (resolved by name from the shared source) —
	// the SAME colour the tier shows on its own availability tile rail and everywhere else.
	const { resolveByName } = useTicketTypeColors();

	if (pools.length === 0) return null;

	return (
		<PunchupStatTileGrid>
			{pools.map((pool) => (
				<SendTicketsPoolTile
					key={pool.poolId}
					pool={pool}
					isExceeded={exceededPoolIds?.has(pool.poolId) ?? false}
					resolveByName={resolveByName}
				/>
			))}
		</PunchupStatTileGrid>
	);
};
